import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * A one-way channel from a page that has taken the screen over for a PURCHASE, out to `AppFrame`,
 * which is where the personal-sale offer band is drawn.
 *
 * It exists because the two checkout lanes sit on opposite sides of the tree, and only one of them is
 * a route. Our own checkout screen IS a route (`/checkout`), so `AppFrame` suppresses the band by
 * reading `router.pathname` and needs nothing from the page. Hosted checkout is NOT: `pages/packages`
 * mounts Stripe's embedded Checkout in a modal and the URL stays `/packages`, so the only thing that
 * knows a buyer is standing on a payment form is that page's own `embeddedSecret` state. A route gate
 * cannot see it — which is exactly how the band survived the `/checkout` fix and went on sitting over
 * the price it advertises, on the lane nearly every buyer is actually served.
 *
 * Deliberately NOT a general "hide the chrome" channel. It carries one claim with one meaning: a
 * purchase surface owns the screen, so do not urge the buyer to go and make that purchase. Anything
 * else that wants the chrome out of the way should say so in its own words rather than borrowing this.
 *
 * A COUNT, not a boolean, so claims compose and release in any order. React mounts the incoming
 * route's tree before unmounting the outgoing one, so with a boolean a leaving page's cleanup would
 * clear an arriving page's claim — putting the band back on top of a live form, which is the whole
 * bug. A count makes the release order irrelevant.
 */
type Channel = {
  suppressed: boolean;
  /** Claim suppression. The returned function releases exactly this claim, and only it. */
  retain: () => () => void;
};

const PersonalSaleSuppressionContext = createContext<Channel>({
  suppressed: false,
  // No provider (the chrome-free routes) means nothing to suppress: the band is not drawn there at all.
  retain: () => () => {},
});

export function PersonalSaleSuppressionProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState(0);
  const retain = useCallback(() => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => Math.max(0, n - 1));
  }, []);
  const value = useMemo<Channel>(() => ({ suppressed: claims > 0, retain }), [claims, retain]);
  return <PersonalSaleSuppressionContext.Provider value={value}>{children}</PersonalSaleSuppressionContext.Provider>;
}

/** What `AppFrame` reads: true while some purchase surface owns the screen. */
export function usePersonalSaleSuppressed(): boolean {
  return useContext(PersonalSaleSuppressionContext).suppressed;
}

/**
 * Held by a page for as long as `active` is true. Idempotent and self-releasing: flipping `active`
 * back to false, or unmounting, drops the claim — so a checkout the buyer closes or cancels brings
 * the band straight back, with no teardown for the page to remember.
 */
export function useSuppressPersonalSale(active: boolean): void {
  const { retain } = useContext(PersonalSaleSuppressionContext);
  useEffect(() => {
    if (!active) return;
    return retain();
  }, [active, retain]);
}
